import { useEffect, useMemo, type ReactNode } from 'react'
import { useRouter } from 'next/router'

import { Spinner } from '@wartek-id/spinner'
import { useToast } from '@wartek-id/toast'

import { PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { useAPIPelaksanaanProgress } from 'api/kinerja/useAPIKinerja'
import { PRAKTIK_KINERJA_STEP_ORDER } from 'app/KinerjaGpkPage/constants/praktik-kinerja-steps'

import type { MultiPeriodPayload } from 'types/kinerja'
import type { TStatePelaksanaan } from 'types/kinerja-pelaksanaan'

type PageGuardGpkPelaksanaanStatusProps = {
  targetStep?: TStatePelaksanaan
  children?: ReactNode
  periodParams: MultiPeriodPayload
}

export function PageGuardGpkPelaksanaanStatus({
  targetStep,
  periodParams,
  children,
}: PageGuardGpkPelaksanaanStatusProps) {
  const toast = useToast()
  const router = useRouter()

  // -----------------------------------------
  // Get pelaksanaan progress data
  // -----------------------------------------
  const { isLoading, data, isSuccess } = useAPIPelaksanaanProgress({
    tipe: 'GPK',
    periodParams: {
      skp_period: periodParams.skpPeriod,
      skp_year: periodParams.skpYear,
    },
    opts: {
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
      refetchOnMount: false, // Prevent double fetch on re-render after invalidation
      refetchOnWindowFocus: false, // Prevent double fetch on window focus
      onError: () => {
        // Redirect to pegawai homepage if something went wrong in fetching pelaksanaan progress data
        router.replace(PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH)
      },
    },
  })

  const isPelaksanaanStarted = useMemo(() => {
    return (
      data?.data?.state !== 'DOCUMENT_PREPARATION' ||
      data?.data?.latestFormState !== 'NOT_CREATED'
    )
  }, [data])

  const isCorrectFormOrder = useMemo(() => {
    // Baypass checking if the target step is not provided
    if (!targetStep) return true

    // Check if the current step (from progress data) is after (or equal to) the target step
    const currentProgressStep = data?.data?.state
    const currentProgressStepOrder =
      PRAKTIK_KINERJA_STEP_ORDER.indexOf(currentProgressStep)
    const targetStepOrder = PRAKTIK_KINERJA_STEP_ORDER.indexOf(targetStep)

    return currentProgressStepOrder >= targetStepOrder
  }, [data, targetStep])

  // -----------------------------------------
  // Redirect to GPK pegawai homepage if pelaksanaan is not started
  // -----------------------------------------
  useEffect(() => {
    if (isSuccess) {
      if (!isPelaksanaanStarted) {
        // If pelaksanaan is not started, redirect to GPK pegawai homepage
        router.replace(PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH)

        if (!isPelaksanaanStarted) {
          toast({
            message: 'Pelaksanaan belum dimulai',
          })
        }
      } else if (!isCorrectFormOrder) {
        // If pelaksanaan is started but form order is incorrect, redirect to the Praktik Kinerja hub
        router.replace(`${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}/pelaksanaan`)

        toast({
          message: 'Lakukan sesuai urutan, ya.',
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isPelaksanaanStarted])

  // -----------------------------------------
  // Render loading state
  // -----------------------------------------
  const shouldRedirect = !isPelaksanaanStarted || !isCorrectFormOrder

  if (isLoading || shouldRedirect) {
    return (
      <div className="bg-gray-10 min-h-full flex pt-20 justify-center">
        <Spinner />
      </div>
    )
  }

  return <>{children}</>
}
